import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MeetingTemplate, useGetDefaultMeetingTemplateQuery, useGetMeetingTemplatesQuery } from '../../store/ai/meetingTemplate'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { TagElement } from '../../components/Tag/Tag'
import { useUpdateSessionMetadataMutation } from '../../store/media/media'
import { RoomSession } from '../../models/Room'
import { parseMetadata } from '../../common/util'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Spinner } from 'react-bootstrap'
import { MeetingTemplateVisibilityChangeConfirmationModal } from './MeetingTemplateVisibilityChangeConfirmationModel'

export interface MeetingTemplateSelectorProps {
  session: RoomSession
  onSessionChange?: (roomSession: RoomSession) => void
}

export const MeetingTemplateSelector: React.FC<MeetingTemplateSelectorProps> = ({ session, onSessionChange }) => {
  const accountId = useAppSelector(selectAccountId)
  const { roomId, id: sessionId } = session
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const {
    privacySource,
    external: { meetingTemplateId = null, aiSuggestedTemplate = null } = {},
    external: externalSessionMetadata,
  } = parseMetadata(session.metadata)
  const { data: meetingTemplates, isFetching } = useGetMeetingTemplatesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, includeInactive: false },
    { refetchOnMountOrArgChange: true },
  )
  const { data: defaultMeetingTemplate } = useGetDefaultMeetingTemplateQuery(
    {
      accountId,
      userId: currentUserId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const [updateRoomSessionMetadata, { isLoading: isUpdatingMetadata }] = useUpdateSessionMetadataMutation()
  const [meetingTemplate, setMeetingTemplate] = useState<MeetingTemplate | null>(null)
  const { tracked } = useTracking()
  const [showModal, setShowModal] = useState(false)
  const [meetingTemplateCandidate, setMeetingTemplateCandidate] = useState<MeetingTemplate | null>(null)

  useEffect(() => {
    if (!meetingTemplates?.data) {
      return
    }
    const sessionMeetingTemplate = meetingTemplateId ? meetingTemplates.data.find((meetingTemplate) => meetingTemplate.id === meetingTemplateId) : null
    setMeetingTemplate(sessionMeetingTemplate ?? defaultMeetingTemplate ?? null)
  }, [meetingTemplates, defaultMeetingTemplate, meetingTemplateId])

  const meetingTemplateOptions = useMemo(
    () =>
      meetingTemplates?.data
        .map(({ id, name }) => ({
          id,
          label: name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)) ?? [],
    [meetingTemplates],
  )

  const UpcomingMeetingTemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick }) => {
      const showTag = defaultMeetingTemplate
        ? defaultMeetingTemplate.id === optionId
        : meetingTemplates?.data.find((meetingTemplate) => meetingTemplate.id === optionId)?.isDefault
      const showAITag = aiSuggestedTemplate === optionId

      return (
        <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex zocks-gap-0.5 align-items-center'>
          {optionLabel}
          {showTag ? <TagElement className='zocks-bg-color-primary zocks-text-white'>Default</TagElement> : null}
          {showAITag ? (
            <TagElement className='bg-transparent zocks-text-primary zocks-border-0.25 border border-1 zocks-border-color-primary'>
              <i className='bi bi-stars'></i>
            </TagElement>
          ) : null}
        </CustomDropdownItemOption>
      )
    },
    [meetingTemplates, defaultMeetingTemplate, aiSuggestedTemplate],
  )

  const _applyMeetingTemplateSelected = (selectedMeetingTemplate: SelectOption) => {
    const { id } = selectedMeetingTemplate
    updateRoomSessionMetadata({
      accountId,
      roomId,
      sessionId,
      metadata: {
        ...externalSessionMetadata,
        meetingTemplateId: id,
      },
    }).then(() => {
      const newMeetingTemplate = meetingTemplates?.data.find((meetingTemplate) => meetingTemplate.id === id) ?? null
      setMeetingTemplate(newMeetingTemplate)
      if (!['manual', 'calendar'].includes(privacySource)) {
        onSessionChange?.({
          ...session,
          isPrivate: newMeetingTemplate?.isPrivate ?? false,
          metadata: JSON.stringify({ ...parseMetadata(session.metadata), privacySource: 'meetingTemplate' }),
        })
      }
      onHide()
    })
  }

  const applyMeetingTemplateSelected = tracked(
    _applyMeetingTemplateSelected,
    { eventName: TrackEvent.ROOM_SESSION_MEETING_TYPE_CHANGED, eventDetails: getRoomTrackingDetails(session) },
    {
      eventDetailsExtractor: ({ id: selectedMeetingTemplateId }: SelectOption) => {
        return {
          selectedMeetingTemplateId,
          source: 'upcoming',
        }
      },
    },
  )

  const handleMeetingTemplateSelected = (selectedMeetingTemplate: SelectOption) => {
    const mt = meetingTemplates?.data.find((t) => t.id === selectedMeetingTemplate.id)
    if (mt && mt.isPrivate !== session.isPrivate && !['manual', 'calendar'].includes(privacySource)) {
      setMeetingTemplateCandidate(mt)
      setShowModal(true)
    } else {
      applyMeetingTemplateSelected(selectedMeetingTemplate)
    }
  }

  const onHide = () => {
    setShowModal(false)
    setMeetingTemplateCandidate(null)
  }

  return isFetching || isUpdatingMetadata ? (
    <div className='d-flex align-items-center justify-content-center p-2'>
      <Spinner size={'sm'} />
    </div>
  ) : (
    <>
      {meetingTemplateCandidate && (
        <MeetingTemplateVisibilityChangeConfirmationModal
          meetingTemplate={meetingTemplateCandidate}
          onConfirm={() => applyMeetingTemplateSelected({ id: meetingTemplateCandidate.id, label: meetingTemplateCandidate.name })}
          show={showModal}
          onHide={onHide}
        />
      )}
      <SelectDropdown
        className='p-2 w-100'
        icon='bi-columns-gap'
        initialSelectedOption={
          meetingTemplate
            ? {
                id: meetingTemplate.id,
                label: meetingTemplate.name,
              }
            : null
        }
        options={meetingTemplateOptions}
        onOptionSelected={handleMeetingTemplateSelected}
        disabled={!meetingTemplates?.data.length}
        DropDownItemOptionComponent={UpcomingMeetingTemplateDropdownItemOption}
      />
    </>
  )
}
